import { AllDrum, AllMelodic, DrumParentCategories } from '@arpeggi/shared'
import { useCallback, useState } from 'react'
import * as Dropzone from 'react-dropzone'
import { useNavigate } from 'react-router-dom'
import { useImmer } from 'use-immer'
import { useAuth } from '../../../../hooks/useAuth'
import useMintGasless from '../../../../mint/MintGasless'
import { getSoundValidationParams } from '../../../../types/sounds'
import { SoundValidation } from './MintSoundModalContent'
import { UploadSoundModalForm } from './MintSoundUploadPage'

// The last 4 mime types should handle most cases for mp3 (there may be edge cases I didnt find).
// I have seen that it can be mis mimed and that would fail here.
const ACCEPTED_AUDIO_FILE_MIME_TYPES = [
  // .wav mime types
  'audio/wav',
  'audio/wave',
  'audio/x-wav',
  'audio/x-pn-wav'

  //   'audio/mpeg',
  //   'audio/mpeg3',
  //   'audio/x-mpeg-3',
  //   'audio/x-mpeg'
]

export const segmentButtonClick = (button: string, location: string) => {
  window.analytics.track('Button Clicked', {
    button: button,
    location: location,
    source: window.location.pathname
  })
}

export const segmentMintSound = (location: string, data: UploadSoundModalForm) => {
  window.analytics.track('Sound Minted', {
    location: location,
    source: window.location.pathname,
    name: data.sampleTitle,
    type: data.soundType,
    key: data.key,
    instrument: data.instrument,
    genres: data.genres,
    length: data.duration,
    bpm: data.bpm
  })
}

const getInstrumentFromFilename = (fileName: string): string | null => {
  const lowercaseFilename = fileName.toLowerCase()

  let fileInstrument: string = null
  AllMelodic.forEach((instrument) => {
    if (lowercaseFilename.includes(instrument.toLowerCase())) {
      fileInstrument = instrument
    }
  })
  AllDrum.forEach((instrument) => {
    if (lowercaseFilename.includes(instrument.toLowerCase())) {
      fileInstrument = instrument
    }
  })

  // Just 'hat' won't get picked up, so we manually have to catch it
  if (!fileInstrument && lowercaseFilename.includes('hat')) {
    fileInstrument = DrumParentCategories.OneShot.Hats[0] // default to "Closed"
  }

  return fileInstrument
}

export type MintSoundModalPages = 'upload' | 'terms' | 'minting' | 'minted'

const useMintSoundModalContent = () => {
  const navigate = useNavigate()
  const { user, getOrSetUserAuthToken } = useAuth()
  const { mintSoundGasless } = useMintGasless()

  const [soundPath, setSoundPath] = useState<string | null>(null)
  const [soundValidations, setSoundValidations] = useImmer<SoundValidation[]>([])

  const [page, setPage] = useState<MintSoundModalPages>('upload')

  // Optional values for manually setting a custom artist or tag.
  const [customArtistAddress, setCustomArtistAddress] = useState<string>(null)
  const [customTags, setCustomTags] = useState<string[]>([])

  const onDropRejected = useCallback((fileRejections: Dropzone.FileRejection[]) => {
    const fileName = fileRejections[0].file.name
    const fileExtension = `.${fileName.slice(fileName.lastIndexOf('.'))}`
    setSoundValidations((draft) => {
      draft.push({
        accepted: false,
        name: fileName,
        size: fileRejections[0].file.size,
        errors: {
          file:
            fileRejections[0].errors[0].message ===
            `File type must be one of ${ACCEPTED_AUDIO_FILE_MIME_TYPES.join(', ')}`
              ? `File type must be .wav, but is ${fileExtension}` // or mp3'
              : fileRejections[0].errors[0].message
        },
        form: { sampleTitle: fileRejections[0].file.name } as UploadSoundModalForm
      })
    })
  }, [])

  const onDropAccepted = useCallback(
    (fileAccepts: File[]) =>
      fileAccepts.forEach(
        async (file) =>
          new Promise((resolve) => {
            const url = URL.createObjectURL(file)
            const audio = new Audio(url)

            audio.onloadedmetadata = async () => {
              if (soundValidations.find((item) => item.name === file.name)) {
                setSoundValidations((draft) => {
                  draft.push({
                    accepted: false,
                    name: file.name,
                    size: file.size,
                    errors: { file: 'Repeat file uploaded' },
                    form: { sampleTitle: file.name } as UploadSoundModalForm
                  })
                })
              } else {
                // // Auto Pitch detection
                // const ab = await file.arrayBuffer()
                // const decodedAudio = await SharedAudioContext.decodeAudioData(ab)
                // const detectPitch = Pitchfinder.DynamicWavelet()
                // const pitch = detectPitch(decodedAudio.getChannelData(0))
                // const kas = new KeyAndScale()
                // const key = kas.fromNote(noteFromPitch(pitch))
                const sampleTitle = file.name.slice(0, file.name.lastIndexOf('.'))
                const instrument = getInstrumentFromFilename(sampleTitle)
                setSoundValidations((draft) => {
                  draft.push({
                    accepted: true,
                    name: file.name,
                    size: file.size,
                    errors: {},
                    form: {
                      file: file,
                      genres: [],
                      soundType: audio.duration < 10 ? 'one-shot' : 'stem',
                      key: AllDrum.includes(instrument) ? 0 : null,
                      bpm: 0,
                      sampleTitle,
                      instrument,
                      duration: audio.duration
                    }
                  })
                })
              }
              resolve(null)
            }
          })
      ),
    []
  )

  const { getRootProps, getInputProps } = Dropzone.useDropzone({
    accept: ACCEPTED_AUDIO_FILE_MIME_TYPES,
    multiple: true,
    onDropRejected,
    onDropAccepted
  })

  const onClickMintSound = async (dataList: UploadSoundModalForm[]) => {
    segmentButtonClick('Mint Sounds (submit)', 'mint modal')
    setPage('minting')

    const isSigned = await getOrSetUserAuthToken()
    // user denied signature
    if (!isSigned) {
      setPage('terms')
      return
    }

    // TODO add error handling here for each sound
    const mintPromises = dataList.map((data) => {
      segmentMintSound('mint sound terms sheet', data)
      return mintSoundGasless({
        file: data.file,
        uploadSoundData: data,
        customArtistAddress,
        customTags
      })
    })
    await Promise.all(mintPromises)

    setPage('minted')
    setSoundPath(`/profile/${user.wallet_address}/created/samples`)
  }

  const onClickNavigateToSound = () => {
    if (soundPath !== null) {
      navigate(soundPath)
    }
  }

  const isValid = (): boolean => {
    if (soundValidations.length === 0) return

    let errorCount: number = 0
    for (let i = 0; i < soundValidations.length; i++) {
      const soundValidation = soundValidations[i]
      if (!soundValidation.accepted) {
        errorCount++
        continue
      }
      if (!soundValidation.form) {
        errorCount++
        continue
      }

      if (!soundValidation.form.soundType) {
        setSoundValidations((draft) => {
          draft[i].errors.soundType = 'Sound type required'
        })
        errorCount++
        continue
      } else {
        setSoundValidations((draft) => {
          draft[i].errors.soundType = null
        })
      }

      const validationParams = getSoundValidationParams(soundValidation.form.soundType)

      if (
        soundValidation.form.duration < validationParams.minDuration ||
        soundValidation.form.duration > validationParams.maxDuration
      ) {
        setSoundValidations((draft) => {
          draft[
            i
          ].errors.duration = `Length of ${soundValidation.form.soundType} files must be between ${validationParams.minDuration} and ${validationParams.maxDuration} seconds`
        })
        errorCount++
        continue
      } else {
        setSoundValidations((draft) => {
          draft[i].errors.duration = null
        })
      }

      if (isNaN(soundValidation.form.bpm)) {
        setSoundValidations((draft) => {
          draft[i].errors.bpm = 'BPM must be a number'
        })
        errorCount++
        continue
      } else if (
        soundValidation.form.bpm < validationParams.minBPM ||
        soundValidation.form.bpm > validationParams.maxBPM
      ) {
        setSoundValidations((draft) => {
          draft[
            i
          ].errors.bpm = `BPM for ${soundValidation.form.soundType}s must be between ${validationParams.minBPM} and ${validationParams.maxBPM}`
        })
        errorCount++
        continue
      } else {
        setSoundValidations((draft) => {
          draft[i].errors.bpm = null
        })
      }

      if (soundValidation.form.key === null || soundValidation.form.key === undefined) {
        setSoundValidations((draft) => {
          draft[i].errors.key = 'Key required'
        })
        errorCount++
        continue
      }

      // allow SFX and Drum samples to have a key of none
      if (
        AllMelodic.includes(soundValidation.form.instrument) &&
        !['fx', 'sfx'].includes(soundValidation.form.instrument.toLowerCase()) &&
        soundValidation.form.key === 0
      ) {
        setSoundValidations((draft) => {
          draft[i].errors.key = 'Key for non-drum samples cannot be "None"'
        })
        errorCount++
        continue
      } else {
        setSoundValidations((draft) => {
          draft[i].errors.key = null
        })
      }

      if (!soundValidation.form.sampleTitle) {
        setSoundValidations((draft) => {
          draft[i].errors.sampleTitle = 'Sample title required'
        })
        errorCount++
        continue
      } else if (soundValidation.form.sampleTitle.length < 4) {
        setSoundValidations((draft) => {
          draft[i].errors.sampleTitle = 'Sample title must be at least 4 characters'
        })
        errorCount++
        continue
      } else if (soundValidation.form.sampleTitle.length > 100) {
        setSoundValidations((draft) => {
          draft[i].errors.sampleTitle = 'Sample title may be max 100 characters'
        })
        errorCount++
        continue
      } else {
        setSoundValidations((draft) => {
          draft[i].errors.sampleTitle = null
        })
      }

      if (soundValidation.form.instrument === null) {
        setSoundValidations((draft) => {
          draft[i].errors.instrument = 'Instrument required'
        })
        errorCount++
        continue
      } else {
        setSoundValidations((draft) => {
          draft[i].errors.instrument = null
        })
      }
    }
    return errorCount === 0
  }

  const handleSubmit = (customArtistAddress?: string, customTags?: string[]) => {
    if (isValid) {
      setCustomArtistAddress(customArtistAddress)
      setCustomTags(customTags)
      setPage('terms')
    }
  }

  const onCancel = () => {
    segmentButtonClick('Cancel Mint Sounds', 'mint terms sheet')
    setPage('upload')
    setSoundValidations([])
  }

  const handleMint = () => {
    onClickMintSound(soundValidations.map((item) => item.form))
  }

  return {
    onClickNavigateToSound,
    soundPath,
    onClickMintSound,
    getInputProps,
    getRootProps,
    soundValidations,
    setSoundValidations,
    handleSubmit,
    handleMint,
    onCancel,
    isValid,
    page
  }
}

export default useMintSoundModalContent
